/**
 * ConsciousnessMilestoneModal — The cinematic moment when your god evolves.
 * 
 * Three tiers of revelation:
 * - 30% (whisper): Dark, mysterious, barely-there glitch text
 * - 60% (observer): Medium intensity, golden observer eye, ethereal
 * - 80% (awakened): DRAMATIC golden explosion, the god has arrived
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Eye, Crown, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Visual configs per milestone level ───
const MILESTONE_VISUALS = {
  whisper: {
    bgClass: 'bg-black/95',
    glowColor: 'rgba(251, 191, 36, 0.05)',
    titleClass: 'text-amber-500/40 text-2xl sm:text-3xl font-light tracking-[0.3em] animate-milestone-flicker',
    subtitleClass: 'text-amber-600/30 text-xs tracking-[0.2em] uppercase',
    descClass: 'text-gray-500 text-sm leading-relaxed max-w-md',
    flavorClass: 'text-amber-500/20 text-xs italic',
    Icon: Eye,
    iconClass: 'w-10 h-10 text-amber-500/20',
    iconGlow: '0 0 40px rgba(251, 191, 36, 0.05)',
    ringClass: 'ring-1 ring-amber-500/10',
    dismissText: 'Continue...',
    dismissClass: 'text-amber-500/30 hover:text-amber-500/50 border-amber-500/10 hover:border-amber-500/20',
  },
  observer: {
    bgClass: 'bg-black/95',
    glowColor: 'rgba(251, 191, 36, 0.15)',
    titleClass: 'text-amber-400/70 text-3xl sm:text-4xl font-semibold tracking-wide',
    subtitleClass: 'text-amber-500/50 text-sm tracking-wider uppercase',
    descClass: 'text-gray-300/70 text-sm leading-relaxed max-w-md',
    flavorClass: 'text-amber-400/40 text-xs italic',
    Icon: Eye,
    iconClass: 'w-14 h-14 text-amber-400/50',
    iconGlow: '0 0 60px rgba(251, 191, 36, 0.15)',
    ringClass: 'ring-1 ring-amber-400/20',
    dismissText: 'I understand',
    dismissClass: 'text-amber-400/50 hover:text-amber-400/70 border-amber-400/20 hover:border-amber-400/40',
  },
  awakened: {
    bgClass: 'bg-black/95',
    glowColor: 'rgba(251, 191, 36, 0.35)',
    titleClass: 'text-amber-300 text-4xl sm:text-5xl font-black tracking-tight animate-milestone-glow',
    subtitleClass: 'text-amber-400 text-base tracking-wider uppercase font-semibold',
    descClass: 'text-gray-200 text-sm leading-relaxed max-w-lg',
    flavorClass: 'text-amber-300/60 text-sm italic font-medium',
    Icon: Crown,
    iconClass: 'w-16 h-16 text-amber-300',
    iconGlow: '0 0 80px rgba(251, 191, 36, 0.4), 0 0 120px rgba(251, 191, 36, 0.15)',
    ringClass: 'ring-2 ring-amber-400/40',
    dismissText: 'Enter the Council',
    dismissClass: 'text-amber-300 hover:text-white border-amber-400/40 hover:border-amber-300 bg-amber-500/10 hover:bg-amber-500/20 font-semibold',
  },
};


export function ConsciousnessMilestoneModal({ milestone, onDismiss }) {
  const [phase, setPhase] = useState(0); // 0=entering, 1=icon, 2=title, 3=desc, 4=full
  const [dismissed, setDismissed] = useState(false);
  const [godName, setGodName] = useState('');
  const [nameSubmitting, setNameSubmitting] = useState(false);
  const [nameError, setNameError] = useState('');
  const [nameSuccess, setNameSuccess] = useState(false);

  const vis = MILESTONE_VISUALS[milestone?.level] || MILESTONE_VISUALS.whisper;
  const IconComp = vis.Icon;
  const isWhisperLevel = milestone?.level === 'whisper';

  useEffect(() => {
    if (!milestone) return;
    const timers = [
      setTimeout(() => setPhase(1), 500),
      setTimeout(() => setPhase(2), 1200),
      setTimeout(() => setPhase(3), 2200),
      setTimeout(() => setPhase(4), 3200),
    ];
    return () => timers.forEach(clearTimeout);
  }, [milestone]);

  const handleRename = useCallback(async () => {
    if (!godName.trim() || godName.trim().length < 2) {
      setNameError('Name must be at least 2 characters');
      return;
    }
    if (godName.trim().length > 30) {
      setNameError('Name must be 30 characters or less');
      return;
    }
    setNameSubmitting(true);
    setNameError('');
    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const uid = payload.sub || payload.user_id;
      const res = await fetch(`${API_URL}/api/avatar/rename`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ user_id: uid, name: godName.trim() }),
      });
      if (res.ok) {
        setNameSuccess(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setNameError(data.detail || 'Failed to rename');
      }
    } catch {
      setNameError('Network error');
    } finally {
      setNameSubmitting(false);
    }
  }, [godName]);

  const handleDismiss = useCallback(async () => {
    setDismissed(true);
    // Dismiss in backend
    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const uid = payload.sub || payload.user_id;
      await fetch(`${API_URL}/api/avatar/milestones/dismiss?user_id=${uid}&milestone_id=${milestone.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
    } catch { /* non-critical */ }
    setTimeout(() => onDismiss?.(), 500);
  }, [milestone, onDismiss]);

  if (!milestone || dismissed) return null;

  return (
    <div className={`fixed inset-0 z-[9999] flex items-center justify-center ${vis.bgClass} animate-milestone-enter`}
         data-testid="consciousness-milestone-modal">
      {/* Background glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
        <div className="w-[400px] h-[400px] rounded-full blur-[120px] opacity-50"
             style={{ background: vis.glowColor }} />
      </div>

      {/* Particles for awakened */}
      {milestone.level === 'awakened' && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          {Array.from({ length: 20 }).map((_, i) => (
            <div key={i}
                 className="absolute w-1 h-1 bg-amber-400 rounded-full animate-milestone-particle"
                 style={{
                   left: `${Math.random() * 100}%`,
                   top: `${Math.random() * 100}%`,
                   animationDelay: `${Math.random() * 3}s`,
                   animationDuration: `${2 + Math.random() * 3}s`,
                   opacity: 0.2 + Math.random() * 0.5,
                 }}
            />
          ))}
        </div>
      )}

      {/* Close button */}
      <button onClick={handleDismiss}
              className="absolute top-6 right-6 text-gray-600 hover:text-gray-400 transition-colors z-10"
              data-testid="milestone-close-btn">
        <X className="w-5 h-5" />
      </button>

      {/* Content */}
      <div className="relative text-center px-6 max-w-xl">
        {/* Icon */}
        <div className={`transition-all duration-1000 mb-6 ${phase >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-black"
               style={{ boxShadow: vis.iconGlow }}>
            <div className={`w-20 h-20 rounded-full flex items-center justify-center ${vis.ringClass}`}>
              <IconComp className={vis.iconClass} />
            </div>
          </div>
        </div>

        {/* Title */}
        <div className={`transition-all duration-1000 mb-2 ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h1 className={vis.titleClass} data-testid="milestone-title">
            {milestone.title}
          </h1>
        </div>

        {/* Subtitle */}
        <div className={`transition-all duration-700 mb-6 ${phase >= 2 ? 'opacity-100' : 'opacity-0'}`}>
          <p className={vis.subtitleClass}>{milestone.subtitle}</p>
        </div>

        {/* Divider */}
        <div className={`transition-all duration-700 mb-6 ${phase >= 3 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex items-center gap-3 justify-center">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-500/20" />
            <Zap className="w-3 h-3 text-amber-500/30" />
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-500/20" />
          </div>
        </div>

        {/* Description */}
        <div className={`transition-all duration-1000 mb-4 ${phase >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <p className={vis.descClass}>{milestone.description}</p>
        </div>

        {/* Flavor text */}
        <div className={`transition-all duration-700 mb-8 ${phase >= 3 ? 'opacity-100' : 'opacity-0'}`}>
          <p className={vis.flavorClass}>{milestone.flavor}</p>
        </div>

        {/* Naming Ritual — only at the 30% whisper milestone */}
        {isWhisperLevel && (
          <div className={`transition-all duration-700 mb-6 ${phase >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {nameSuccess ? (
              <div className="text-amber-400/70 text-sm" data-testid="naming-ritual-success">
                The name has been inscribed. Your god remembers.
              </div>
            ) : (
              <div className="max-w-xs mx-auto space-y-3" data-testid="naming-ritual-form">
                <p className="text-amber-500/40 text-xs tracking-wider uppercase">Give it a name...</p>
                <input
                  type="text"
                  value={godName}
                  onChange={(e) => setGodName(e.target.value)}
                  placeholder="Name your god"
                  maxLength={30}
                  className="w-full bg-black/60 border border-amber-500/20 rounded-lg px-4 py-2.5 text-amber-300/80 text-center text-sm placeholder-amber-500/20 focus:outline-none focus:border-amber-500/40 transition-colors"
                  data-testid="naming-ritual-input"
                />
                {nameError && <p className="text-red-400/60 text-xs">{nameError}</p>}
                <button
                  onClick={handleRename}
                  disabled={nameSubmitting || !godName.trim()}
                  className="px-6 py-2 rounded-full border border-amber-500/20 text-amber-500/50 hover:text-amber-400/70 hover:border-amber-500/30 text-xs transition-all disabled:opacity-30"
                  data-testid="naming-ritual-submit"
                >
                  {nameSubmitting ? 'Inscribing...' : 'Inscribe Name'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Avatar name + consciousness */}
        <div className={`transition-all duration-700 mb-8 ${phase >= 4 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/5 border border-amber-500/10">
            <span className="text-xs text-amber-400/60 font-medium">{nameSuccess ? godName.trim() : milestone.avatar_name}</span>
            <span className="text-[10px] text-amber-500/30">|</span>
            <span className="text-xs text-amber-500/40">{milestone.consciousness}% consciousness</span>
          </div>
        </div>

        {/* Dismiss button */}
        <div className={`transition-all duration-700 ${phase >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <button onClick={handleDismiss}
                  className={`px-8 py-2.5 rounded-full border text-sm transition-all duration-300 ${vis.dismissClass}`}
                  data-testid="milestone-dismiss-btn">
            {vis.dismissText}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes milestone-enter {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-milestone-enter {
          animation: milestone-enter 1s ease-out forwards;
        }
        @keyframes milestone-flicker {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.6; }
          25%, 75% { opacity: 0.3; }
        }
        .animate-milestone-flicker {
          animation: milestone-flicker 3s ease-in-out infinite;
        }
        @keyframes milestone-glow {
          0%, 100% { text-shadow: 0 0 20px rgba(251, 191, 36, 0.3); }
          50% { text-shadow: 0 0 40px rgba(251, 191, 36, 0.5), 0 0 60px rgba(251, 191, 36, 0.2); }
        }
        .animate-milestone-glow {
          animation: milestone-glow 2s ease-in-out infinite;
        }
        @keyframes milestone-particle {
          0% { transform: translateY(0) scale(1); opacity: 0; }
          20% { opacity: 1; }
          100% { transform: translateY(-200px) scale(0); opacity: 0; }
        }
        .animate-milestone-particle {
          animation: milestone-particle 3s ease-out infinite;
        }
      `}</style>
    </div>
  );
}
